import React, { createContext, useContext, useState, useEffect } from "react";
import Cookies from "js-cookie";
import { useAuth } from "./AuthContext";
import { useShopContext } from "./ShopContext";
import { BranchProduct, Order, PaginatedResponse, Product } from "../types";
import api from "../api/client";
import { Address, CartItem } from "../types/cart";
import { useNotification } from "./NotificationContext";
import { useNavigate } from "react-router-dom";
import { useAnalytics } from "./AnalyticsContext";

// ✅ تحديث واجهة CartItem لتشمل colorId و colorName


export interface DeliverySetting {
  id: number;
  store: { id: number; name: string };
  city: { id: number; name: string; country: string };
  estimated_days: number;
  delivery_fee: string;
  is_active: boolean;
}

export interface PaymentOrderResponse {
  id: number;
  customer: number;
  total: number;
  status: string;
  payment_status: string;
  payment_method: string;
  address: {
    city: string;
    neighborhood: string;
    center: string;
    street: string;
    mobile: string;
    compound: string;
    building_or_house: string;
    latitude: number;
    longitude: number;
  };
  items: {
    id: number;
    product: {
      id: number;
      name: string;
      sku: string;
      description: string;
      short_description: string;
      base_price: string;
      categories: any[];
      created_at: string;
      images: {
        id: number;
        product: number;
        image: string;
        image_url: string;
        alt_text: string;
      }[];
      is_featured: boolean;
      total_sold: number;
      badge: string;
      average_rating: number | null;
    };
    quantity: number;
    price: string;
    color_id?: number; // ✅ إضافة
  }[];
  created_at: string;
  updated_at: string;
  payment_url: string;
  uuid: string;
}

interface StoreDeliverySetting {
  id: number;
  store: number;
  city: {
    id: number;
    name: string;
  };
  estimated_days: number;
  delivery_fee: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// ✅ واجهة الكوبون
export interface Coupon {
  code: string;
  type: string;
  value: string;
}

// ✅ نتيجة التحقق من الكوبون
export interface CouponValidationResult {
  success: boolean;
  message: string;
  data?: {
    coupon: Coupon;
    order: {
      before_discount: string;
      discount_amount: string;
      after_discount: string;
      savings_percentage: string;
    };
    applicable_items_count: number;
    validity: string;
  };
}

// ✅ تفاصيل الخصم
export interface CouponDiscountDetails {
  discountAmount: number;
  orderBefore: number;
  orderAfter: number;
  savingsPercentage: string;
  discountType?: "percentage" | "fixed";
  discountValue?: number;
}

// ✅ تحديث واجهة CartContextType
interface CartContextType {
  cartItems: CartItem[];
  orders: Order[];
  
  // ✅ تحديث دوال السلة لتدعم colorId
  addToCart: (product: BranchProduct, colorId?: number) => void;
  updateQuantity: (
    productId: number,
    branchId: number,
    quantity: number,
    colorId?: number // ✅ إضافة colorId اختياري
  ) => void;
  removeItem: (
    productId: number, 
    branchId: number, 
    colorId?: number // ✅ إضافة colorId اختياري
  ) => void;

  // ✅ دوال الكوبون
  validateCoupon: (code: string) => Promise<CouponValidationResult>;
  applyCoupon: (orderId: number) => Promise<{ success: boolean; message: string }>;
  getAvailableCoupons: () => Promise<Coupon[]>;
  currentCoupon: Coupon | null;
  couponDiscount: CouponDiscountDetails | null;
  removeCoupon: () => Promise<void>;
  getDiscountDetails: () => CouponDiscountDetails | null;

  // ✅ دوال الطلبات
  checkout: (
    address: Address,
    paymentMethod: string,
    storeIds?: number[],
    phoneNum?: string
  ) => Promise<void>;
  clearCartItemsForStores: (storeIds: number[]) => void;
  fetchOrders: () => Promise<void>;
  updateOrderStatus: (orderId: number, status: string) => Promise<void>;
  fetchStoreDeliverySettings: (storeId: number) => Promise<any[]>;
  
  // ✅ بيانات وعرض الحالة
  storeDeliverySettings: DeliverySetting[];
  loading: boolean;
  error: string | null;
  lastPaymentUrl: string | null;
  
  // ✅ دوال مساعدة
  getCartTotal: () => number;
  getCartItemsCount: () => number;
  getItemQuantity: (productId: number, branchId: number, colorId?: number) => number;
  getItemsByStore: (storeId: number) => CartItem[];
}

// ✅ helper function لتحويل branchId إلى storeId
const getStoreIdFromBranch = (
  branchId: number,
  branches: any[]
): number | null => {
  const branch = branches.find((b) => b.id === branchId);
  return branch ? Number(branch.store) : null;
};

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { selectedBranch, branches } = useShopContext();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { notify } = useNotification();
  const [storeDeliverySettings, setStoreDeliverySettings] = useState<
    DeliverySetting[]
  >([]);
  const [lastPaymentUrl, setLastPaymentUrl] = useState<string | null>(null);
  const { trackEvent } = useAnalytics();

  // ✅ حالات الكوبون
  const [currentCoupon, setCurrentCoupon] = useState<Coupon | null>(null);
  const [couponDiscount, setCouponDiscount] =
    useState<CouponDiscountDetails | null>(null);

  // ✅ تحميل السلة من الكوكيز
  useEffect(() => {
    const savedCart = Cookies.get("cart");
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (error) {
        console.error("Error parsing cart from cookies:", error);
        Cookies.remove("cart");
      }
    }
  }, []);

  // ✅ حفظ السلة في الكوكيز
  useEffect(() => {
    Cookies.set("cart", JSON.stringify(cartItems), { expires: 7 });
  }, [cartItems]);

  // ✅ مزامنة السلة مع الباكند
  useEffect(() => {
    if (user && cartItems.length > 0) {
      syncCartWithBackend();
    }
  }, [user]);

  // ✅ تحميل الطلبات
  useEffect(() => {
    if (user) {
      fetchOrders();
    }
  }, [user]);

  // ✅ تتبع تحديثات السلة
  useEffect(() => {
    if (cartItems.length === 0) return;

    trackEvent("cart_updated", {
      user_id: user?.id || null,
      cart_items: cartItems.map((item) => ({
        product_id: item.productId,
        name: item.name,
        quantity: item.quantity,
        branch_id: item.branchId,
        color_id: item.colorId,
        price: item.price,
      })),
    });
  }, [cartItems, user]);

  // ✅ الدالة الرئيسية: addToCart مع دعم colorId
const addToCart = (product: BranchProduct, colorId?: number) => {
  setCartItems((prev) => {
    const selectedColor = colorId
      ? product.colors?.find((c) => c.id === colorId)
      : undefined;
    const colorName = selectedColor ? selectedColor.color_name : undefined;
    console.log("Adding to cart with colorId:", colorId, "Color Name:", colorName);
    const existingItem = prev.find(
      (item) =>
        item.productId === product.product.id &&
        item.branchId === product.branch.id &&
        item.colorId === selectedColor?.id &&
        item.colorName === colorName
    );

    if (existingItem) {
      return prev.map((item) =>
        item.productId === product.product.id &&
        item.branchId === product.branch.id &&
        item.colorId === selectedColor?.id && 
        item.colorName === colorName
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
    }

const newItem: CartItem = {
  productId: product.product.id,
  name: product.product.name,
  price: product.price,
  quantity: 1,
  branchId: product.branch.id,

  colorId: selectedColor?.id,
  colorName: selectedColor?.color_name,
  image:
    selectedColor?.images?.[0]?.image ||
    product.product.images?.[0]?.image,
};


    trackEvent('product_added_to_cart', {
      product_id: product.product.id,
      product_name: product.product.name,
      branch_id: product.branch.id,
      color_id: selectedColor?.id,
      color_name: selectedColor?.color_name,
      price: product.price,
    });

    return [...prev, newItem];
  });

  setError(null);
  notify('success', `تمت إضافة ${product.product.name} إلى السلة`);
};


  // ✅ تحديث quantity مع دعم colorId
  const updateQuantity = (
    productId: number,
    branchId: number,
    quantity: number,
    colorId?: number
  ) => {
    if (quantity < 1) return;
    
    setCartItems((prev) =>
      prev.map((item) =>
        item.productId === productId && 
        item.branchId === branchId && 
        item.colorId === colorId
          ? { ...item, quantity }
          : item
      )
    );
    
    // 🔹 تتبع حدث تحديث الكمية
    trackEvent("cart_item_quantity_updated", {
      product_id: productId,
      branch_id: branchId,
      color_id: colorId,
      new_quantity: quantity
    });
  };

  // ✅ حذف عنصر مع دعم colorId
const removeItem = (
  productId: number,
  branchId: number,
colorId?: number
) => {
  let removedCount = 0;

  setCartItems((prev) =>
    prev.filter((item) => {
      if (item.productId !== productId || item.branchId !== branchId) return true;

      if (colorId !== undefined) {
        if (item.colorId === colorId) removedCount++;
        return item.colorId !== colorId;
      }

      if (item.colorId === undefined || item.colorId === null) removedCount++;
      return item.colorId !== undefined && item.colorId !== null;
    })
  );

  trackEvent("cart_item_removed", { product_id: productId, branch_id: branchId, color_id: colorId });

  if (removedCount > 0) {
    notify("info", "تم حذف المنتج من السلة");
  } else {
    notify("warning", "لا يوجد عنصر للحذف");
  }
};


  // ✅ حذف عناصر متاجر محددة
  const clearCartItemsForStores = (storeIds: number[]) => {
    if (!storeIds.length || !branches.length) return;

    setCartItems((prev) => {
      return prev.filter((item) => {
        const storeId = getStoreIdFromBranch(item.branchId, branches);
        return storeId === null || !storeIds.includes(storeId);
      });
    });

    notify("success", `تم حذف المنتجات من ${storeIds.length} متجر`);
  };

  // ✅ مزامنة السلة مع الباكند
  const syncCartWithBackend = async () => {
    try {
      setLoading(true);
      await api.post("/cart/sync/", { 
        items: cartItems.map(item => ({
          productId: item.productId,
          branchId: item.branchId,
          quantity: item.quantity,
          colorId: item.colorId
        }))
      });
    } catch (err) {
      console.error("Sync cart error:", err);
      setError("Failed to sync cart with server");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Checkout مع دعم colorId
  const checkout = async (
    address: Address,
    paymentMethod: string,
    storeIds?: number[],
    phoneNum?: string
  ) => {
    if (!user) {
      Cookies.set("redirectAfterLogin", "/cart");
      window.location.href = "/login";
      return;
    }

    try {
      setLoading(true);

      // ✅ فلترة المنتجات بناءً على storeIds
      let itemsToCheckout = cartItems;
      if (storeIds && storeIds.length > 0 && branches.length > 0) {
        itemsToCheckout = cartItems.filter((item) => {
          const storeId = getStoreIdFromBranch(item.branchId, branches);
          return storeId !== null && storeIds.includes(storeId);
        });
      }

      if (itemsToCheckout.length === 0) {
        notify("warning", "لم يتم اختيار أي منتجات للشراء");
        return;
      }

      // ✅ إعداد بيانات الطلب مع colorId
      const orderItems = itemsToCheckout.map((item) => ({
        product_id: item.productId,
        quantity: item.quantity,
        price: item.price,
        branch_id: item.branchId,
        color_id: item.colorId,
      }));

      const response = await api.post<PaymentOrderResponse>("/orders/", {
        customer_id: user.id,
        phone_number: phoneNum,
        items: orderItems,
        address: address,
        payment_method: paymentMethod,
        coupon_code: currentCoupon?.code || null,
      });

      // ✅ بعد نجاح الطلب، حذف المنتجات المشتراة
      if (response.status === 200 || response.status === 201) {
        if (storeIds && storeIds.length > 0) {
          clearCartItemsForStores(storeIds);
        } else {
          setCartItems([]);
          Cookies.remove("cart");
        }
      }

      setLastPaymentUrl(response?.data?.payment_url);

      // ✅ معالجة حالات النجاح
      if (response.status === 200 || response.status === 201) {
        // 🔹 تتبع حدث الشراء
        trackEvent("checkout_completed", {
          user_id: user.id,
          order_id: response.data.id,
          items_count: orderItems.length,
          total_amount: response.data.total,
          payment_method: paymentMethod,
        });

        // ✅ الدفع الإلكتروني
        if (paymentMethod !== "cash_on_delivery" && response.data?.payment_url) {
          window.location.href = response.data.payment_url;
          return;
        } else {
          window.location.href = '/profile';
        }

        notify("success", "تم إنشاء طلبك بنجاح");
        return;
      } else if (response.status === 500) {
        notify("error", "حدث خطأ أثناء معالجة الدفع. الرجاء المحاولة مرة أخرى.");
      }

      notify("error",  "حدث خطأ ما.");
    } catch (err: any) {
      console.error("Checkout error:", err.response?.data || err.message);

      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.stock ||
        "فشل عملية الشراء. الرجاء المحاولة مرة أخرى.";

      notify("error", errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // ✅ دالة للحصول على تفاصيل الخصم
  const getDiscountDetails = (): CouponDiscountDetails | null => {
    return couponDiscount;
  };

  // ✅ دوال الكوبون
  const validateCoupon = async (
    code: string
  ): Promise<CouponValidationResult> => {
    try {
      setLoading(true);

      const cart_items = cartItems.map((item) => ({
        product_id: item.productId,
        branch_id: item.branchId,
        quantity: item.quantity,
        color_id: item.colorId,
      }));

      const response = await api.post<CouponValidationResult>(
        "/wallet/coupons/validate/",
        {
          code: code.toUpperCase(),
          cart_items,
        }
      );

      if (response.data.success && response.data.data) {
        const { coupon, order } = response.data.data;

        setCurrentCoupon(coupon);

        setCouponDiscount({
          discountAmount: parseFloat(order.discount_amount),
          orderBefore: parseFloat(order.before_discount),
          orderAfter: parseFloat(order.after_discount),
          savingsPercentage: order.savings_percentage,
        });

        notify("success", response.data.message);
        
        // 🔹 تتبع حدث التحقق من الكوبون
        trackEvent("coupon_validated", {
          coupon_code: code,
          discount_amount: parseFloat(order.discount_amount)
        });
      } else {
        setCurrentCoupon(null);
        setCouponDiscount(null);
        notify("error", response.data.message);
      }

      return response.data;
    } catch (err: any) {
      console.error("Coupon validation error:", err);

      setCurrentCoupon(null);
      setCouponDiscount(null);

      const msg = err.response?.data?.message || "خطأ أثناء التحقق من الكوبون";
      notify("error", msg);

      return {
        success: false,
        message: msg,
      };
    } finally {
      setLoading(false);
    }
  };

  const applyCoupon = async (
    orderId: number
  ): Promise<{ success: boolean; message: string }> => {
    try {
      setLoading(true);

      if (!currentCoupon) {
        throw new Error("No coupon selected");
      }

      const response: any = await api.post("/wallet/coupons/apply/", {
        order_id: orderId,
        coupon_code: currentCoupon.code,
      });

      if (response.data.success) {
        notify("success", response.data.message);

        // 🔹 تتبع حدث تطبيق الكوبون
        trackEvent("coupon_applied", {
          order_id: orderId,
          coupon_code: currentCoupon.code,
          discount_amount: couponDiscount?.discountAmount || 0,
        });
      } else {
        notify("error", response.data.message);
      }

      return response.data;
    } catch (err: any) {
      console.error("Apply coupon error:", err);

      const msg = err.response?.data?.message || "فشل تطبيق الكوبون";
      notify("error", msg);

      return {
        success: false,
        message: msg,
      };
    } finally {
      setLoading(false);
    }
  };

  const getAvailableCoupons = async (): Promise<Coupon[]> => {
    try {
      setLoading(true);

      const response: any = await api.get("/wallet/coupons/available/");

      if (response.data.success) {
        return response.data.coupons || [];
      }

      return [];
    } catch (err: any) {
      console.error("Get available coupons error:", err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const removeCoupon = async (): Promise<void> => {
    return new Promise((resolve) => {
      setCurrentCoupon(null);
      setCouponDiscount(null);
      notify("info", "تم إزالة الكوبون");
      
      // 🔹 تتبع حدث إزالة الكوبون
      trackEvent("coupon_removed", {});
      
      resolve();
    });
  };

  // ✅ دوال الطلبات
  const fetchOrders = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const response = await api.get<PaginatedResponse<Order>>("/orders/");
      setOrders(response.data.results || []);
    } catch (err) {
      setError("Failed to load orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId: number, status: string) => {
    try {
      setLoading(true);
      await api.patch(`/orders/${orderId}/`, { status });
      await fetchOrders();
    } catch (err: any) {
      setError(
        err.response?.data?.detail ||
          `Failed to update order status to ${status}`
      );
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchStoreDeliverySettings = async (
    storeId: number
  ): Promise<any[]> => {
    try {
      setLoading(true);

      const res = await api.get<PaginatedResponse<DeliverySetting>>(
        `/store-delivery-settings/?store=${storeId}`
      );

      const results = res?.data?.results || [];
      setStoreDeliverySettings(results);

      return results;
    } catch (err: any) {
      console.error("Error fetching store delivery settings:", err);
      setError("Failed to fetch store delivery settings.");
      return [];
    } finally {
      setLoading(false);
    }
  };

  // ✅ دوال مساعدة
  const getCartTotal = (): number => {
    const subtotal = cartItems.reduce((total, item) => 
      total + (item.price * item.quantity), 0
    );
    
    if (couponDiscount) {
      return couponDiscount.orderAfter;
    }
    
    return subtotal;
  };

  const getCartItemsCount = (): number => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getItemQuantity = (productId: number, branchId: number, colorId?: number): number => {
    const item = cartItems.find(
      item => item.productId === productId && 
              item.branchId === branchId && 
              item.colorId === colorId
    );
    return item ? item.quantity : 0;
  };

  const getItemsByStore = (storeId: number): CartItem[] => {
    return cartItems.filter((item) => {
      const itemStoreId = getStoreIdFromBranch(item.branchId, branches);
      return itemStoreId === storeId;
    });
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        orders,
        addToCart,
        updateQuantity,
        removeItem,
        checkout,
        clearCartItemsForStores,
        getDiscountDetails,
        validateCoupon,
        applyCoupon,
        getAvailableCoupons,
        currentCoupon,
        couponDiscount,
        removeCoupon,
        fetchOrders,
        updateOrderStatus,
        fetchStoreDeliverySettings,
        storeDeliverySettings,
        loading,
        error,
        lastPaymentUrl,
        // ✅ دوال مساعدة
        getCartTotal,
        getCartItemsCount,
        getItemQuantity,
        getItemsByStore,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};